/** Additional reference-kit assemblies, using the same dimensions/collision emitter. */
import type { AssetNode, AssetVec3 } from '../proceduralAssets.js';
import type { ActivitySlot, CanopyPlan, FrontierPlacePlan } from './frontierTown.js';
import * as pmath from '../pmath.js';
export interface FrontierBuilder {
  part(id:string,build:()=>void):void;
  node(kind:AssetNode['kind'],params:number[],p:AssetVec3,mat:number,r?:AssetVec3):AssetNode;
  box(size:AssetVec3,p:AssetVec3,mat:number,solid?:boolean,r?:AssetVec3):void;
  floor(w:number,d:number,x?:number,z?:number,h?:number):void;
  post(x:number,z:number,h?:number,y?:number):void;
  roof(w:number,d:number,eave:number,x?:number,z?:number,pitch?:number,quarterTurn?:number):void;
  barrel(x:number,z:number,base?:number):void;
  crate(x:number,z:number):void;
  slot(id:string,activity:ActivitySlot['activity'],p:AssetVec3,approach:AssetVec3,heading?:number):void;
  awning(id:string,x:number,w:number,backZ:number,frontZ:number,backY:number,frontY:number,attachment:CanopyPlan['attachment'],ridge?:number,material?:number):void;
}
export function frontierExtra(kind:'chapel'|'watchtower'|'well',b:FrontierBuilder,d:FrontierPlacePlan['design']):AssetVec3 {
  const {node,box,floor,post,roof,slot}=b;
  const banner=(x:number,y:number,z:number)=>{
    box([.95,2,.07],[x,y,z],5);box([.36,.36,.09],[x,y+.15,z+.05],0,false,[0,0,Math.PI/4]);
    box([1.2,.1,.14],[x,y+1.05,z],1);
  };
  if(kind==='chapel'){
    const w=d.homeWidth-.4,depth=7.8,front=depth/2,doorX=.65,doorW=1.9,h=4.4;
    floor(w+.6,depth+.6);
    box([w,h,.25],[0,.3+h/2,-front],0,true);
    for(const x of [-w/2,w/2])box([.25,h,depth],[x,.3+h/2,0],0,true);
    const left=doorX-doorW/2+w/2,right=w/2-doorX-doorW/2;
    box([left,h,.25],[-w/2+left/2,.3+h/2,front],0,true);
    box([right,h,.25],[w/2-right/2,.3+h/2,front],0,true);
    box([doorW,h-2.8,.25],[doorX,.3+2.8+(h-2.8)/2,front],0,true);
    for(const x of [doorX-doorW/2,doorX+doorW/2])box([.16,2.85,.35],[x,1.725,front+.1],1);
    box([doorW+.2,.16,.35],[doorX,3.12,front+.1],1);
    const slope=pmath.sin(d.roofPitch)/pmath.cos(d.roofPitch),rise=(depth+.8)/2*slope;
    for(const x of [-w/2,w/2]){
      const g=node('box',[.25,rise,depth+.8],[x,.3+h+rise/2,0],0);
      g.modifiers=[{type:'taper',axis:'y',factor:.01}];
    }
    roof(w+.8,depth+.8,h+.4);
    floor(doorW+.5,1.1,doorX,front+.6,.3);
    floor(doorW+.7,.8,doorX,front+1.3,.15);
    {
      const tx=-w/2-1,th=7.5+(d.crystalHeight-3)*.6;
      floor(2.4,2.8,tx,-.8);
      box([1.8,th-1.7,2],[tx,.3+(th-1.7)/2,-.8],0,true);
      for(const x of [tx-.7,tx+.7])for(const z of [-1.5,-.1])post(x,z,1.8,th-1.4);
      box([2.1,.18,2.1],[tx,th+.4,-.8],0);
      node('cone',[1.9,.9],[tx,th+.85,-.8],2,[0,Math.PI/4,0]);
      node('cone',[.38,.6],[tx,th-.45,-.8],7);
      box([.06,.6,.06],[tx,th-.02,-.8],7);
      banner(w/2-1.2,3.1,front+.21);
      for(const x of [-w/2,w/2]){
        box([.3,1.2,.7],[x,2.7,-1.3],5);
        box([.4,.13,.9],[x,2.06,-1.3],4);
      }
      box([2,.65,.75],[0,.625,-front+.8],1,true);
      slot('visit','inspect',[doorX,.3,front-.4],[doorX,0,front+2.6],Math.PI);
    }
    return [doorX,0,front+1.7];
  }
  if(kind==='watchtower'){
    const h=5.8+d.crystalHeight*.4;
    floor(6.2,5.2,.6,0);floor(1.2,1.8,2.65,3.2);
    for(const x of [-1.8,1.8])for(const z of [-1.8,1.8])post(x,z,h,.3);
    for(const z of [-1.8,1.8])for(const r of [-.8,.8])box([5.4,.18,.18],[0,3.2,z],1,false,[0,0,r]);
    for(const x of [-1.8,1.8])for(const r of [-.8,.8])box([.18,.18,5.4],[x,3.2,0],1,false,[r,0,0]);
    box([4.5,.24,4.5],[0,h+.3,0],1);
    for(const x of [-1.9,1.9])for(const z of [-1.9,1.9])post(x,z,2.4,h+.3);
    for(const z of [-2,2])box([4.3,.15,.15],[0,h+1.3,z],1);
    for(const x of [-2,2])box([.15,.15,4.3],[x,h+1.3,0],1);
    roof(4.9,4.9,h+2.6);banner(-.7,h-.8,2.05);
    // A ladder reaches the deck; climbing is a separate movement capability.
    for(const x of [2.35,2.95])box([.12,h+.7,.12],[x,h/2+.35,2.3],1,false,[-.28,0,0]);
    for(let i=0;i<15;i++)box([.72,.09,.09],[2.65,.35+i*h/15,3.3-i*2/15],1);
    slot('watch','inspect',[2.65,.3,3.4],[2.65,0,4.9],Math.PI);
    return [2.65,0,3.9];
  }
  if(kind==='well'){
    const r=1.1+(d.homeWidth-7.6)*.12;
    floor(4,4,0,0,.16);
    for(let i=0;i<14;i++){
      const a=i/14*Math.PI*2;
      box([.52,.85,.28],[pmath.sin(a)*r,.585,pmath.cos(a)*r],4,true,[0,a,0]);
    }
    node('cylinder',[r-.17,r-.17,.035],[0,.2,0],7);
    const peak=3.25+1.8*pmath.sin(d.roofPitch)/pmath.cos(d.roofPitch);
    for(const x of [-1.5,1.5])post(x,0,peak-.26,.16);
    box([3.3,.18,.18],[0,peak-.1,0],1);
    roof(3.7,3.6,3.25);
    node('cylinder',[.16,.16,2.9],[0,2.5,0],1,[0,0,Math.PI/2]);
    box([.045,1.8,.045],[0,1.55,0],1);
    box([.15,.6,.15],[1.7,2.3,0],1);box([.45,.12,.12],[1.85,2.02,0],1);
    node('cylinder',[.23,.18,.45],[-1.45,.385,1.2],1);
    slot('draw-water','inspect',[0,0,2.05],[0,0,3.7],Math.PI);
    return [0,0,2.3];
  }
  throw new Error(`Unsupported frontier extra: ${kind}`);
}
